import { FC } from "react"
import { z } from "zod"

export type SearchContext = {
    orgId: string | undefined
    signal?: AbortSignal
    active: boolean
}

export type SearchRequestContext = Omit<SearchContext, "active">

export type SearchFn<Data> = (query: string, ctx: SearchContext) => Promise<Data[]>

type BaseSearchPlugin<Data extends object, Name extends string> = {
    name: Name
    displayName: string
    useSearch: () => SearchFn<Data>
    dataSchema: z.ZodType<Data>
    Component: FC<Data & { onAfterSelect: () => void }>
    getKey: (data: Data) => string
    getSearchableText: (data: Data, ctx: SearchRequestContext) => string
}

export type SearchPlugin<Data extends object, Name extends string = string> = BaseSearchPlugin<
    Data,
    Name
> & {
    FullPanelHeaderComponent?: never
    FullPanelResultComponent?: never
    FullPanelEmptyComponent?: never
    fullPanelSearchPlaceholder?: never
}
export type ActivatableSearchPlugin<
    Data extends object,
    Name extends string = string,
> = BaseSearchPlugin<Data, Name> & {
    FullPanelHeaderComponent: FC
    FullPanelResultComponent: FC<Data & { onAfterSelect: () => void }>
    FullPanelEmptyComponent?: FC<{ query: string }>
    fullPanelSearchPlaceholder?: string
}

export type AnySearchPlugin<Data extends object, Name extends string = string> =
    | SearchPlugin<Data, Name>
    | ActivatableSearchPlugin<Data, Name>

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function definePlugin<const T extends SearchPlugin<any, string>>(plugin: T): T {
    return plugin
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function defineActivatablePlugin<const T extends ActivatableSearchPlugin<any, string>>(
    plugin: T
): T {
    return plugin
}

export type SearchResultEntry<Data extends object = object> = {
    plugin: AnySearchPlugin<Data>
    data: Data
}

export type RecentSearchResultEntry<Data extends object = object> = SearchResultEntry<Data> & {
    timestamp: string
}

export type SearchResultComponent<T> = T & { onAfterSelect: () => void }
